/* Seizure & Aura Logger — live stopwatch, classification, triggers, post-ictal. */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Stop, Trash, Lightning, Pause, DeviceMobile, DeviceMobileCamera } from "@phosphor-icons/react";
import health from "./healthApi";
import { useShakeTrigger, requestShakePermission } from "./useShakeTrigger";

const CLASSIFICATIONS = ["Aura only", "Focal Aware", "Focal Impaired Awareness", "Bilateral Tonic-Clonic"];
const TRIGGER_TAGS = ["Stress", "Poor sleep", "Missed dose", "Alcohol", "Screen/flashing lights", "Menstrual", "Skipped meal", "Illness"];

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

export default function SeizureLogger({ onSaved, onShakeStart }) {
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [classification, setClassification] = useState("Focal Aware");
  const [triggers, setTriggers] = useState([]);
  const [postIctal, setPostIctal] = useState("");
  const [severity, setSeverity] = useState(0);
  const [auraOnly, setAuraOnly] = useState(false);
  const [past, setPast] = useState([]);
  const [shakeArmed, setShakeArmed] = useState(false);
  const tick = useRef(null);

  const start = () => {
    setStartedAt(Date.now());
    setElapsed(0);
    setRunning(true);
  };

  // Hands-free start via violent shake pattern
  useShakeTrigger({
    enabled: shakeArmed && !running,
    onShake: () => {
      // Auto-tag "Detected via shake" trigger for the record
      setTriggers((cur) => (cur.includes("Detected via shake") ? cur : [...cur, "Detected via shake"]));
      start();
      // Loud audible + haptic ping so the user knows it captured
      try { navigator.vibrate?.([120, 60, 120]); } catch {}
      toast.info("Shake detected — stopwatch started · fall-detect armed");
      // Let the parent kick off the 60s fall-detect escalation
      onShakeStart?.();
    },
  });

  const armShake = async () => {
    const ok = await requestShakePermission();
    if (!ok) return toast.error("Motion permission denied — enable in browser settings");
    setShakeArmed(true);
    toast.success("Hands-free trigger armed — a violent shake will start logging");
  };

  const load = () => health.get("/seizures?days=30").then((r) => setPast(r.data));
  useEffect(() => { load(); return () => clearInterval(tick.current); }, []);

  useEffect(() => {
    if (running) {
      tick.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      }, 250);
    } else if (tick.current) {
      clearInterval(tick.current);
    }
    return () => clearInterval(tick.current);
  }, [running, startedAt]);

  const stopAndSave = async () => {
    setRunning(false);
    try {
      const saved = await health.post("/seizures", {
        started_at: new Date(startedAt).toISOString(),
        duration_sec: elapsed,
        classification,
        triggers,
        post_ictal_notes: postIctal,
        post_ictal_severity: severity,
        aura_only: auraOnly,
      });
      toast.success(`Event logged · ${fmt(elapsed)}`);
      setElapsed(0); setStartedAt(null); setPostIctal(""); setSeverity(0); setAuraOnly(false); setTriggers([]);
      load();
      onSaved?.(saved.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const toggleTrigger = (t) => setTriggers((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

  const del = async (id) => {
    if (!confirm("Delete this event?")) return;
    await health.delete(`/seizures/${id}`);
    load();
  };

  return (
    <div data-testid="nl-seizure-tab">
      <div className="mb-4">
        <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-accent)" }}>TEMPORAL LOBE AURA / SEIZURE</div>
        <div className="font-display text-2xl font-black" style={{ color: "var(--nl-text)" }}>Live event logger</div>
      </div>

      <div className="rounded-2xl p-6 text-center mb-4" style={{ background: "var(--nl-panel)", border: `2px solid ${running ? "var(--nl-danger)" : "var(--nl-line)"}` }}>
        <div className="mb-3 flex items-center justify-between gap-2 rounded px-3 py-2"
             style={{ background: shakeArmed ? "rgba(72,202,228,0.12)" : "var(--nl-bg-2)",
                      border: `1px solid ${shakeArmed ? "var(--nl-accent)" : "var(--nl-line)"}` }}>
          <div className="flex items-center gap-2">
            <DeviceMobileCamera size={16} weight={shakeArmed ? "fill" : "regular"}
                                color={shakeArmed ? "var(--nl-accent)" : "var(--nl-muted)"} />
            <div className="text-left">
              <div className="font-label text-[10px] tracking-widest"
                   style={{ color: shakeArmed ? "var(--nl-accent)" : "var(--nl-muted)" }}>
                {shakeArmed ? "HANDS-FREE ARMED" : "HANDS-FREE TRIGGER"}
              </div>
              <div className="text-[10px]" style={{ color: "var(--nl-muted)" }}>
                {shakeArmed ? "Shake auto-starts stopwatch · fall-detect on stillness" : "Shake auto-starts · 60s stillness → auto-ICE"}
              </div>
            </div>
          </div>
          <button
            data-testid="nl-shake-arm"
            onClick={() => shakeArmed ? setShakeArmed(false) : armShake()}
            className="text-[10px] font-label tracking-widest px-3 py-1.5 rounded"
            style={{
              background: shakeArmed ? "transparent" : "var(--nl-accent)",
              color: shakeArmed ? "var(--nl-accent)" : "#001018",
              border: `1px solid var(--nl-accent)`,
            }}
          >
            {shakeArmed ? "DISARM" : "ARM"}
          </button>
        </div>

        <div className="font-display font-black tabular-nums" style={{ color: running ? "var(--nl-danger)" : "var(--nl-accent)", fontSize: 64, lineHeight: 1 }}>
          {fmt(elapsed)}
        </div>
        <div className="font-label text-[10px] tracking-widest mt-2" style={{ color: "var(--nl-muted)" }}>
          {running ? "RECORDING · TAP STOP WHEN OVER" : "STOPWATCH"}
        </div>
        {!running ? (
          <button
            data-testid="nl-seizure-start"
            onClick={start}
            className="mt-6 h-14 w-full rounded-xl font-label text-sm tracking-widest flex items-center justify-center gap-2"
            style={{ background: "var(--nl-danger)", color: "#fff" }}
          >
            <Lightning size={18} weight="fill" /> START — I'M HAVING ONE
          </button>
        ) : (
          <button
            data-testid="nl-seizure-stop"
            onClick={stopAndSave}
            className="mt-6 h-14 w-full rounded-xl font-label text-sm tracking-widest flex items-center justify-center gap-2 nl-ice-pulse"
            style={{ background: "var(--nl-accent)", color: "#001018" }}
          >
            <Stop size={18} weight="fill" /> STOP & LOG
          </button>
        )}
      </div>

      <div className="rounded-lg p-4 mb-3" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
        <div className="font-label text-[10px] tracking-widest mb-2" style={{ color: "var(--nl-muted)" }}>CLASSIFICATION</div>
        <div className="flex flex-wrap gap-2">
          {CLASSIFICATIONS.map((c) => (
            <button
              key={c}
              onClick={() => { setClassification(c); setAuraOnly(c === "Aura only"); }}
              className="text-xs px-3 py-1.5 rounded-full font-label tracking-widest"
              style={{
                background: classification === c ? "var(--nl-accent)" : "var(--nl-bg-2)",
                color: classification === c ? "#001018" : "var(--nl-text)",
                border: `1px solid ${classification === c ? "var(--nl-accent)" : "var(--nl-line)"}`,
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg p-4 mb-3" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
        <div className="font-label text-[10px] tracking-widest mb-2" style={{ color: "var(--nl-muted)" }}>TRIGGERS</div>
        <div className="flex flex-wrap gap-2">
          {TRIGGER_TAGS.map((t) => (
            <button
              key={t}
              data-testid={`nl-trigger-${t.replace(/\W+/g, '-')}`}
              onClick={() => toggleTrigger(t)}
              className="text-xs px-3 py-1.5 rounded-full font-label tracking-widest"
              style={{
                background: triggers.includes(t) ? "var(--nl-warn)" : "var(--nl-bg-2)",
                color: triggers.includes(t) ? "#000" : "var(--nl-text)",
                border: `1px solid ${triggers.includes(t) ? "var(--nl-warn)" : "var(--nl-line)"}`,
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg p-4 mb-4" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
        <div className="font-label text-[10px] tracking-widest mb-2" style={{ color: "var(--nl-muted)" }}>POST-ICTAL SEVERITY (0-10)</div>
        <input type="range" min="0" max="10" value={severity} onChange={(e) => setSeverity(Number(e.target.value))} className="w-full" />
        <div className="text-xs mt-1" style={{ color: "var(--nl-accent)" }}>Level {severity}/10 · {["fine","tired","foggy","confused","aphasic","exhausted","disoriented","memory loss","unable to speak","near-fugue","totally spaced"][severity]}</div>
        <textarea value={postIctal} onChange={(e) => setPostIctal(e.target.value)} rows={2} placeholder="Post-ictal notes (memory, speech, mood)"
                  className="nl-input mt-3" style={{ resize: "none" }} />
      </div>

      <div>
        <div className="font-label text-[10px] tracking-widest mb-2" style={{ color: "var(--nl-muted)" }}>RECENT EVENTS (30d)</div>
        <div className="space-y-2">
          {past.length === 0 && <div className="text-xs" style={{ color: "var(--nl-muted)" }}>No events logged. That's good.</div>}
          {past.map((e) => (
            <div key={e.id} className="rounded-lg p-3 flex items-start justify-between gap-2"
                 style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
              <div className="min-w-0">
                <div className="text-sm font-display font-bold" style={{ color: "var(--nl-text)" }}>
                  {e.classification} · <span style={{ color: "var(--nl-accent)" }}>{fmt(e.duration_sec)}</span>
                </div>
                <div className="text-[11px]" style={{ color: "var(--nl-muted)" }}>{e.started_at?.slice(0, 16).replace("T"," ")}</div>
                {(e.triggers || []).length > 0 && (
                  <div className="text-[11px] mt-1" style={{ color: "var(--nl-warn)" }}>
                    {(e.triggers || []).join(" · ")}
                  </div>
                )}
              </div>
              <button onClick={() => del(e.id)} style={{ color: "var(--nl-danger)" }}><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
